import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { GroupMembershipOption } from '../models/group-detail.model';
import { GroupsService } from '../groups.service';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ValidateDomain } from '../../shared/validators/domain-validator';
import { Subscription } from 'rxjs';
import { AlertService } from '../../shared/alert/alert.service';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatLegacySelectModule as MatSelectModule } from "@angular/material/legacy-select";
import { MatLegacyRadioModule as MatRadioModule } from "@angular/material/legacy-radio";
import { MatLegacySlideToggleModule as MatSlideToggleModule } from "@angular/material/legacy-slide-toggle";
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'xcdrs-join-group-dialog',
  templateUrl: './join-group-dialog.component.html',
  styleUrls: ['./join-group-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule, TranslateModule, MatSelectModule, MatRadioModule,
    MatSlideToggleModule, MatButtonModule, MatFormFieldModule, MatInputModule,
    FormsModule, ReactiveFormsModule, MatIconModule
  ]
})

export class JoinGroupDialogComponent implements OnInit, OnDestroy {

  groupMembershipOption = GroupMembershipOption;
  text = '';
  title = '';
  desc = '';
  domainsFull = '';

  allowAnyDomain: boolean = false;
  loading: boolean = false;
  isSubGroup: boolean = false;

  form: UntypedFormGroup;
  codeCtrl: UntypedFormControl = new UntypedFormControl('',
    [Validators.required, Validators.minLength(4), Validators.maxLength(32)])
  isEmailVerification: boolean = false;
  private alertSub: Subscription = new Subscription();

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private groupsService: GroupsService,
    private fb: UntypedFormBuilder,
    private alertService: AlertService,
    private dialogRef: MatDialogRef<JoinGroupDialogComponent>,
    private translateService: TranslateService,
    @Inject(
      MAT_DIALOG_DATA) public dialogData:
      {
        membershipOption: GroupMembershipOption,
        groupName: string,
        groupId: string,
        allowedDomains: string[],
        invitationKey?: string,
        callFrom?: string,
        isSubgroup?: boolean
      }
  ) {
    if (this.dialogData?.isSubgroup) {
      this.isSubGroup = this.dialogData.isSubgroup;
      this.title = 'JoinGroup.JoinASubGroup';
      this.text = 'JoinGroupDialog.JoinSubGroupConfirmationMsg';
      return;
    }
    switch (dialogData.membershipOption) {
      case GroupMembershipOption.AdminApprovalRequired: {
        this.title = 'JoinGroupDialog.JoinRequest';
        this.text = 'JoinGroupDialog.RequestGroupConfirmationMsg';
        this.desc = 'JoinGroupDialog.VerifyIdentity';
        break;
      }
      case GroupMembershipOption.JoinByEmailAddressDomain: {
        this.title = 'JoinGroupDialog.JoinByEmail';
        this.text = this.dialogData.allowedDomains.length
          ?
          `JoinGroupDialog.UseWorkEmailMsg`
          : 'JoinGroupDialog.EnterEmailMsg';
        break;
      }
      case GroupMembershipOption.AnyoneCanJoin: {
        this.title = 'JoinGroup.JoinAGroup';
        this.text = 'JoinGroupDialog.JoinGroupConfirmationMsg';
        break;
      }
    }
    if (this.dialogData.allowedDomains.length) {
      // Allowed domains:<br/>
      this.domainsFull = this.translateService.instant('GroupMembershipPage.AllowedDomains') + ':<br/>';
      this.allowAnyDomain = this.dialogData.allowedDomains.some(d => d == '*');
      this.domainsFull +=
        this.dialogData.allowedDomains.map(el => '- ' + el).join('<br/>');
    }
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
    if (this.dialogData.allowedDomains.length && !this.allowAnyDomain) {
      this.form.get('email').setValidators([ValidateDomain(this.dialogData.allowedDomains), Validators.email]);
    }
  }

  ngOnDestroy(): void {
    this.alertSub.unsubscribe();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  join(): void {
    this.loading = true;
    this.groupsService.joinGroup(this.dialogData.groupId, null, this.dialogData?.invitationKey)
      .pipe(finalize(() => {
        this.loading = false;
      }))
      .subscribe((res) => {
        this.dialogRef.close(res);
        if (res.toLocaleLowerCase() == 'joined') {
          this.alertService.success(
            `You have successfully joined the group “${this.dialogData.groupName}”.`,
            {
              timeout: 3000
            });
          if (this.dialogData?.callFrom != 'MEMBERSHIP' && this.dialogData?.callFrom !== 'GROUP_GUARD') {
            this.groupsService.setGroupData(this.dialogData.groupId);
            // this.router.navigate(['/']);
            // Navigate to '/groups/join-as' with the query parameter "groupId"
            this.router.navigate(['/groups/join-as'], {
              queryParams: { groupId: this.dialogData.groupId }
            });
          }
        }

      }, err => {
        this.dialogRef.close();
        if (err?.error?.IdenediExceptionType != 'NOT_ALLOWED_COUNTRY') {
          this.alertService.error(
            "ExdrCommon.ServerError",
            {
              timeout: 3000
            });
        }
      });
  }

  sendVerficationEmail() {
    this.loading = true;
    this.groupsService
      .sendEmailCode(this.form.value.email)
      .pipe(finalize(() => {
        this.loading = false;
      }))
      .subscribe(() => {
        this.isEmailVerification = true;
      },
        err => {
          this.alertService.error("ExdrCommon.ServerError", {
            timeout: 3000
          });
        });
  }

  verifyCode() {
    this.loading = true;
    this.groupsService.verifyEmailCode(this.dialogData.groupId, this.form.value.email, this.codeCtrl.value, this.dialogData?.invitationKey)
      .pipe(finalize(() => {
        this.loading = false;
      }))
      .subscribe(() => {
        this.alertService.success(
          `You have successfully joined the group “${this.dialogData.groupName}”.`,
          {
            timeout: 3000
          });

        if (this.dialogData?.callFrom === 'GROUP_GUARD') {
          this.dialogRef.close('Joined');
        } else {
          this.groupsService.setGroupData(this.dialogData.groupId);
          if (this.dialogData?.callFrom != 'MEMBERSHIP') {
            this.router.navigate(['/']);
          }
          this.dialogRef.close();
        }
      }, err => {
        this.alertService.error(
          `Please enter valid code.`,
          {
            timeout: 3000
          });
      });
  }

  joinSubGroup() {
    this.dialogRef.close(true);
  }
}
